import React, { useState, useEffect } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import * as Checkbox from "@radix-ui/react-checkbox";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import { X, Check, Search } from "lucide-react";

interface Choice {
  _id: string;
  label: string;
  price: number;
  default: boolean;
}

interface CustomizationOption {
  _id: string;
  name: string;
  required: boolean;
  multiple: boolean;
  multipleAddons?: boolean;
  choices: Choice[];
}

interface PresetItem {
  _id: string;
  name: string;
  description: string;
  categoryId: string;
  category: string;
  isActive: boolean;
  customizationOptions: CustomizationOption[];
}

interface PrerequisitesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: PresetItem[];
  onSave: (selectedItems: string[]) => void;
  selectedIds: string[];
}

const PrerequisitesDialog: React.FC<PrerequisitesDialogProps> = ({
  open,
  onOpenChange,
  data,
  onSave,
  selectedIds,
}) => {
  const [selectedItems, setSelectedItems] = useState<Set<string>>(
    new Set(selectedIds)
  );
  const [searchQuery, setSearchQuery] = useState<string>("");

  const handleCheckboxChange = (itemId: string) => {
    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleSave = () => {
    onSave(Array.from(selectedItems));
    onOpenChange(false);
  };

  useEffect(() => {
    setSelectedItems(new Set(selectedIds));
    if (open) setSearchQuery("");
  }, [selectedIds, open]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[75] bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-[50%] top-[50%] z-[80] w-full max-w-2xl translate-x-[-50%] translate-y-[-50%] bg-white p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <Dialog.Title className="text-2xl font-bold text-black">
              Preset
            </Dialog.Title>
            <Dialog.Close className="rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:pointer-events-none">
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </Dialog.Close>
          </div>

          {/* Search bar */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search presets..."
              className="w-full pl-9 pr-4 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-black"
            />
          </div>

          <ScrollArea.Root className="w-full h-[460px] overflow-hidden">
            <ScrollArea.Viewport className="w-full h-full">
              <div className="space-y-4 pr-4">
                {data
                  .filter((item) =>
                    item.name.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                  .map((item) => (
                  <div
                    key={item._id}
                    onClick={() => handleCheckboxChange(item._id)}
                    className={`
    p-4 rounded-lg transition-colors cursor-pointer
    hover:bg-gray-50
    ${selectedItems.has(item._id) ? "bg-gray-50" : ""}
  `}
                  >
                    <div className="flex items-start space-x-3">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCheckboxChange(item._id);
                        }}
                        className={`
    relative h-5 w-5 shrink-0 mt-1
    flex items-center justify-center
    rounded border
    transition-all duration-150
    ${
      selectedItems.has(item._id)
        ? "bg-black border-black"
        : "bg-white border-black"
    }
  `}
                      >
                        {selectedItems.has(item._id) && (
                          <svg
                            viewBox="0 0 24 24"
                            className="h-4 w-4 text-white"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </button>

                      <div className="flex-1">
                        <label
                          htmlFor={item._id}
                          className="font-semibold text-lg text-black cursor-pointer"
                        >
                          {item.name}
                        </label>
                        <p className="text-sm text-gray-600 mt-1">
                          {item.description}
                        </p>

                        <div className="mt-3 space-y-2">
                          {item.customizationOptions.map((option) => (
                            <div key={option._id} className="ml-4">
                              <div className="flex items-center gap-2 mb-2">
                                {option.required && (
                                  <span className="text-xs bg-black text-white px-2 py-0.5 rounded">
                                    Required
                                  </span>
                                )}
                                {option.multiple && (
                                  <span className="text-xs bg-gray-200 text-black px-2 py-0.5 rounded">
                                    Multiple
                                  </span>
                                )}
                                {option.multipleAddons && (
                                  <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded">
                                    Multi-Addon
                                  </span>
                                )}
                              </div>
                              <div className="space-y-1">
                                {option.choices.map((choice) => (
                                  <div
                                    key={choice._id}
                                    className="flex items-center justify-between text-sm text-gray-700 ml-2"
                                  >
                                    <span className="flex items-center">
                                      <span className="w-1.5 h-1.5 bg-black rounded-full mr-2"></span>
                                      {choice.label}
                                      {choice.default && (
                                        <span className="text-xs ml-1 text-gray-500">
                                          (default)
                                        </span>
                                      )}
                                    </span>
                                    <span className="font-medium text-black">
                                      ${choice.price.toFixed(2)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  ))}
              </div>
            </ScrollArea.Viewport>
            <ScrollArea.Scrollbar
              className="flex select-none touch-none p-0.5 bg-gray-100 transition-colors duration-[160ms] ease-out hover:bg-gray-200 data-[orientation=vertical]:w-2.5 data-[orientation=horizontal]:flex-col data-[orientation=horizontal]:h-2.5"
              orientation="vertical"
            >
              <ScrollArea.Thumb className="flex-1 bg-gray-400 rounded-[10px] relative before:content-[''] before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2 before:w-full before:h-full before:min-w-[44px] before:min-h-[44px]" />
            </ScrollArea.Scrollbar>
          </ScrollArea.Root>

          <div className="flex justify-end space-x-3 border-t border-gray-200 pt-4 mt-6">
            <button
              onClick={() => {
                setSelectedItems(new Set(selectedIds));
                onOpenChange(false);
              }}
            >
              Cancel
            </button>

            <button
              onClick={handleSave}
              className="px-4 py-2 bg-black text-white hover:bg-gray-800 transition-colors font-medium rounded-lg"
            >
              Save ({selectedItems.size} selected)
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default PrerequisitesDialog;
